import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Table, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import './Settings.scss';

/**
 * AdminTermine ermöglicht Administratoren die systemweite Einsicht, Überwachung 
 * und Stornierung (Löschung) von Terminen/Lerneinheiten der Benutzer.
 */
export default function AdminTermine() {
  const { token } = useAuth();

  // Fehlermeldungen / Erfolgsmeldungen
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Direktsuche per Benutzer-ID
  const [directUserId, setDirectUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserLoading, setSelectedUserLoading] = useState(false);
  const [selectedUserError, setSelectedUserError] = useState('');

  // Dozenten-Suchliste
  const [dozenten, setDozenten] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dozentenLoading, setDozentenLoading] = useState(false);

  // Termine des ausgewählten Nutzers
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState('');

  // Lösch-Bestätigung (Termin-ID)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  /**
   * Hilfsfunktion zur Ermittlung detaillierter API-Fehlermeldungen.
   */
  const handleResponseError = async (response, defaultMessage) => {
    let errorMsg = defaultMessage;
    try {
      const errData = await response.json();
      errorMsg = errData.message || errData.error || errorMsg;
    } catch (e) {
      if (response.statusText) {
        errorMsg = `${defaultMessage} (${response.status} - ${response.statusText})`;
      } else {
        errorMsg = `${defaultMessage} (Status-Code: ${response.status})`;
      }
    }
    return new Error(errorMsg);
  };

  /**
   * Lädt die Dozenten zur Schnellauswahl.
   */
  const fetchDozenten = async (query = '') => {
    if (!token) return;
    setDozentenLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/suche/dozent/${encodeURIComponent(query || '@')}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw await handleResponseError(response, 'Dozenten konnten nicht geladen werden.');
      }
      const data = await response.json();
      setDozenten(data.sort((a, b) => a.id - b.id));
    } catch (err) {
      setError(err.message || 'Fehler beim Suchen der Dozenten.');
    } finally {
      setDozentenLoading(false);
    }
  };

  useEffect(() => {
    fetchDozenten('');
  }, [token]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchDozenten(searchTerm);
  };

  /**
   * Effekt zur automatischen Abrufung des Benutzers bei Änderung der directUserId (mit Debounce).
   */
  useEffect(() => {
    if (!directUserId || parseInt(directUserId, 10) <= 0) {
      setSelectedUser(null);
      setSelectedUserError('');
      setAppointments([]);
      return;
    }

    const fetchDirectUser = async () => {
      setSelectedUserLoading(true);
      setSelectedUserError('');
      setSelectedUser(null);
      setAppointments([]);
      try {
        const response = await fetch(`${getBackendUrl()}/chat/${parseInt(directUserId, 10)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          if (response.status === 404) {
            setSelectedUserError('Kein aktiver Benutzer mit dieser ID gefunden');
          } else {
            setSelectedUserError('Fehler beim Abrufen des Benutzers');
          }
          return;
        }
        const data = await response.json();
        if (data && data.andererNutzer) {
          if (data.andererNutzer.rolle === 'deleted') {
            setSelectedUserError('Benutzer ist deaktiviert (soft-deleted)');
          } else {
            setSelectedUser(data.andererNutzer);
          }
        } else {
          setSelectedUserError('Kein aktiver Benutzer mit dieser ID gefunden');
        }
      } catch (err) {
        setSelectedUserError('Fehler beim Verbinden mit dem Server');
      } finally {
        setSelectedUserLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchDirectUser();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [directUserId, token]);

  /**
   * Effekt zum Laden der Termine, wenn ein Benutzer ausgewählt wurde.
   */
  useEffect(() => {
    if (!selectedUser || !token) {
      setAppointments([]);
      return;
    }

    const fetchAppointments = async () => {
      setAppointmentsLoading(true);
      setAppointmentsError('');
      try {
        const response = await fetch(`${getBackendUrl()}/termine/${selectedUser.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          throw await handleResponseError(response, 'Termine des Benutzers konnten nicht abgerufen werden.');
        }
        const data = await response.json();
        setAppointments(data.sort((a, b) => b.id - a.id));
      } catch (err) {
        setAppointmentsError(err.message || 'Fehler beim Laden der Termine.');
      } finally {
        setAppointmentsLoading(false);
      }
    };

    fetchAppointments();
  }, [selectedUser, token]);

  /**
   * Löscht einen Termin aus dem System.
   */
  const handleDeleteTermin = async (appointmentId) => {
    if (!token) return;
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const response = await fetch(`${getBackendUrl()}/termine/${appointmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw await handleResponseError(response, 'Termin konnte nicht gelöscht werden.');
      }

      setSuccess(`Termin #${appointmentId} wurde erfolgreich gelöscht.`);
      setDeleteConfirmId(null);
      
      // Termine neu laden
      if (selectedUser) {
        const res = await fetch(`${getBackendUrl()}/termine/${selectedUser.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAppointments(data.sort((a, b) => b.id - a.id));
        }
      }
    } catch (err) {
      setError(err.message || 'Löschen des Termins fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Hilfsfunktionen zur Formatierung
  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return isoString;
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) + ' Uhr';
    } catch (e) {
      return isoString;
    }
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null) return '-';
    try {
      const num = typeof price === 'number' ? price : parseFloat(price);
      if (isNaN(num)) return price;
      return num.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    } catch (e) {
      return price + ' €';
    }
  };

  const translateRole = (role) => {
    switch (role?.toLowerCase()) {
      case 'tutor':
      case 'dozent':
        return 'Dozent';
      case 'student':
      case 'schueler':
        return 'Schüler';
      case 'admin':
        return 'Admin';
      default:
        return role || 'Nutzer';
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'offen':
        return <Badge bg="warning" className="text-dark" style={{ fontSize: '0.8rem' }}>Offen</Badge>;
      case 'geplant':
        return <Badge bg="primary" style={{ fontSize: '0.8rem' }}>Geplant</Badge>;
      case 'passiert':
        return <Badge bg="info" className="text-dark" style={{ fontSize: '0.8rem' }}>Stattgefunden</Badge>;
      case 'abgelehnt':
        return <Badge bg="danger" style={{ fontSize: '0.8rem' }}>Abgelehnt</Badge>;
      case 'abgeschlossen':
        return <Badge bg="success" style={{ fontSize: '0.8rem' }}>Abgeschlossen</Badge>;
      case 'cancelled':
        return <Badge bg="secondary" style={{ fontSize: '0.8rem' }}>Storniert</Badge>;
      default:
        return <Badge bg="light" className="text-dark" style={{ fontSize: '0.8rem' }}>{status}</Badge>;
    }
  };

  const getZahlungBadge = (zahlungsstatus) => {
    switch (zahlungsstatus?.toLowerCase()) {
      case 'offen':
        return <Badge bg="danger" style={{ fontSize: '0.8rem' }}>Offen</Badge>;
      case 'bezahlt':
        return <Badge bg="success" style={{ fontSize: '0.8rem' }}>Bezahlt</Badge>;
      default:
        return <Badge bg="light" className="text-dark" style={{ fontSize: '0.8rem' }}>{zahlungsstatus}</Badge>;
    }
  };

  return (
    <div className="vintage-settings p-2 p-sm-3 p-md-4" style={{ maxWidth: '100%', backgroundColor: '#fcfaf8', minHeight: '100%', borderRadius: '12px' }}>
      <h1 className="page-header" style={{ color: '#2c3e50', fontWeight: '700' }}>Termine verwalten</h1>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Row className="mb-4">
        {/* Direktsuche per ID */}
        <Col lg={5} className="mb-4 mb-lg-0">
          <Card className="settings-card shadow-sm border-0 h-100 mb-0">
            <Card.Header className="bg-white border-bottom">
              <span style={{ fontWeight: '600' }}>Direktsuche nach ID</span>
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-between">
              <div>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label mb-1">Benutzer-ID eingeben</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="z. B. 3"
                    value={directUserId}
                    onChange={(e) => setDirectUserId(e.target.value)}
                    disabled={submitting}
                    className="form-control py-1 px-2"
                    size="sm"
                    style={{ height: '38px', borderRadius: '8px' }}
                  />
                  <Form.Text className="text-muted small">
                    Geben Sie eine Schüler- oder Dozenten-ID ein, um deren Termine anzuzeigen.
                  </Form.Text>
                </Form.Group>

                {/* Vorschau der ausgewählten Person */}
                {(selectedUserLoading || selectedUser || selectedUserError) && (
                  <div className="p-3 border rounded mt-3" style={{ backgroundColor: '#fdfbf9', borderColor: '#eae5e0', borderRadius: '12px' }}>
                    {selectedUserLoading && (
                      <div className="d-flex align-items-center text-muted">
                        <Spinner animation="border" size="sm" className="me-2" style={{ color: '#b95c50' }} />
                        <span>Suche Benutzer-Details...</span>
                      </div>
                    )}
                    {selectedUserError && (
                      <div className="text-danger small fw-semibold">
                        <i className="bi bi-exclamation-circle me-1"></i> {selectedUserError}
                      </div>
                    )}
                    {selectedUser && (
                      <div className="d-flex align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle fw-bold" style={{ width: '40px', height: '40px', backgroundColor: '#b95c50' }}>
                          {selectedUser.vollerName?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.95rem' }}>
                            {selectedUser.vollerName}
                            <Badge style={{ backgroundColor: '#b95c50', fontSize: '0.7rem' }}>
                              {translateRole(selectedUser.rolle)}
                            </Badge>
                          </div>
                          <div className="text-muted small">
                            <i className="bi bi-envelope me-1"></i> {selectedUser.email}
                          </div>
                          <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                            Benutzer-ID: #{selectedUser.id}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedUser && (
                <div className="mt-3 pt-3 border-top d-flex justify-content-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setDirectUserId('');
                      setSelectedUser(null);
                    }}
                    style={{ borderRadius: '8px' }}
                  >
                    Auswahl aufheben
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Dozenten-Schnellauswahl */}
        <Col lg={7}>
          <Card className="settings-card shadow-sm border-0 h-100 mb-0">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
              <span style={{ fontWeight: '600' }}>Dozenten-Schnellauswahl</span>
              <Form onSubmit={handleSearchSubmit} className="d-flex gap-2" style={{ maxWidth: '250px' }}>
                <Form.Control
                  type="text"
                  placeholder="Name / E-Mail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="sm"
                  style={{ borderRadius: '8px' }}
                  className="form-control"
                />
                <Button type="submit" size="sm" style={{ backgroundColor: '#b95c50', borderColor: '#b95c50', borderRadius: '8px' }}>
                  Suchen
                </Button>
              </Form>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {dozentenLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" style={{ color: '#b95c50' }} />
                  <div className="text-muted small mt-2">Dozenten werden geladen...</div>
                </div>
              ) : dozenten.length === 0 ? (
                <div className="text-center py-4 text-muted small">
                  Keine Dozenten gefunden.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle mb-0 text-nowrap" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#fdfaf7', borderBottom: '1px solid #eae5e0' }}>
                        <th style={{ padding: '8px 16px', color: '#7f8c8d' }}>ID</th>
                        <th style={{ padding: '8px 16px', color: '#7f8c8d' }}>Name</th>
                        <th style={{ padding: '8px 16px', color: '#7f8c8d' }}>E-Mail</th>
                        <th style={{ padding: '8px 16px', color: '#7f8c8d', textAlign: 'right' }}>Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dozenten.map((dozent) => (
                        <tr key={dozent.id} style={{ borderBottom: '1px solid #eae5e0' }}>
                          <td style={{ padding: '8px 16px', fontWeight: '500' }}>#{dozent.id}</td>
                          <td style={{ padding: '8px 16px', fontWeight: '500' }}>{dozent.vollerName}</td>
                          <td style={{ padding: '8px 16px', color: '#7f8c8d' }}>{dozent.email}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                            <Button
                              size="sm"
                              variant={selectedUser?.id === dozent.id ? "success" : "outline-primary"}
                              onClick={() => setDirectUserId(dozent.id.toString())}
                              style={{ 
                                fontSize: '0.75rem', 
                                borderRadius: '6px',
                                borderColor: selectedUser?.id === dozent.id ? '#198754' : '#b95c50', 
                                color: selectedUser?.id === dozent.id ? '#fff' : '#b95c50',
                                backgroundColor: selectedUser?.id === dozent.id ? '#198754' : 'transparent',
                              }}
                            >
                              {selectedUser?.id === dozent.id ? 'Ausgewählt' : 'Wählen'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Terminliste */}
      {selectedUser && (
        <Card className="settings-card shadow-sm border-0">
          <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2 py-3">
            <div>
              <span style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                Termine von {selectedUser.vollerName} ({translateRole(selectedUser.rolle)})
              </span>
              <div className="text-muted small" style={{ fontWeight: 'normal' }}>
                Gefundene Termine: {appointments.length}
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {appointmentsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="md" className="mb-2" style={{ color: '#b95c50' }} />
                <div className="text-muted">Termine werden abgerufen...</div>
              </div>
            ) : appointmentsError ? (
              <div className="p-4 text-center">
                <Alert variant="danger" className="mb-0">{appointmentsError}</Alert>
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-5 text-muted">
                Dieser Benutzer hat aktuell keine Termine im System.
              </div>
            ) : (
              <>
                {/* Desktop-Ansicht: Tabelle */}
                <div className="table-responsive d-none d-lg-block">
                  <Table hover className="align-middle mb-0" style={{ backgroundColor: 'transparent' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#fdfaf7', borderBottom: '1px solid #eae5e0' }}>
                        <th style={{ padding: '1.25rem 1.5rem', width: '80px', color: '#7f8c8d', fontSize: '0.85rem' }}>ID</th>
                        <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Fach (Skill)</th>
                        <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Datum / Zeit</th>
                        <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Dauer</th>
                        <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Teilnehmer</th>
                        <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Preis</th>
                        <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Status</th>
                        <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Zahlung</th>
                        <th style={{ padding: '1.25rem 1.5rem', width: '220px', color: '#7f8c8d', fontSize: '0.85rem', textAlign: 'right' }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((termin) => {
                        const partner = selectedUser.rolle?.toLowerCase() === 'tutor' ? termin.schueler : termin.tutor;
                        const partnerLabel = selectedUser.rolle?.toLowerCase() === 'tutor' ? 'Schüler' : 'Dozent';

                        return (
                          <tr key={termin.id} style={{ borderBottom: '1px solid #eae5e0' }}>
                            <td style={{ padding: '1.25rem 1.5rem', fontWeight: '500' }}>#{termin.id}</td>
                            <td style={{ padding: '1.25rem 1.5rem' }}>
                              <div style={{ fontWeight: '600', color: '#2c3e50' }}>{termin.skillName}</div>
                              {termin.beschreibung && (
                                <div className="text-muted small text-truncate" style={{ maxWidth: '200px' }} title={termin.beschreibung}>
                                  {termin.beschreibung}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '1.25rem 1.5rem', color: '#2c3e50', fontSize: '0.9rem' }}>
                              {formatDateTime(termin.datumZeit)}
                            </td>
                            <td style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d' }}>
                              {termin.dauerMinuten} Min.
                            </td>
                            <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.9rem' }}>
                              <div style={{ fontWeight: '500' }}>{partner?.vollerName || 'Unbekannt'}</div>
                              <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                {partnerLabel}-ID: #{partner?.id || '?' }
                              </div>
                            </td>
                            <td style={{ padding: '1.25rem 1.5rem', fontWeight: '600', color: '#2c3e50' }}>
                              {formatPrice(termin.preis)}
                            </td>
                            <td style={{ padding: '1.25rem 1.5rem' }}>
                              {getStatusBadge(termin.status)}
                            </td>
                            <td style={{ padding: '1.25rem 1.5rem' }}>
                              {getZahlungBadge(termin.zahlungsstatus)}
                            </td>
                            <td style={{ padding: '1.25rem 1.5rem', textAlign: 'right' }}>
                              {deleteConfirmId === termin.id ? (
                                <div className="d-flex justify-content-end align-items-center gap-1">
                                  <span className="text-danger fw-bold me-1" style={{ fontSize: '0.8rem' }}>Sicher?</span>
                                  <Button 
                                    variant="danger" 
                                    size="sm" 
                                    onClick={() => handleDeleteTermin(termin.id)}
                                    disabled={submitting}
                                    style={{ borderRadius: '6px', fontSize: '0.75rem', padding: '4px 10px' }}
                                  >
                                    Ja, löschen
                                  </Button>
                                  <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    onClick={() => setDeleteConfirmId(null)}
                                    disabled={submitting}
                                    style={{ borderRadius: '6px', fontSize: '0.75rem', padding: '4px 10px' }}
                                  >
                                    Nein
                                  </Button>
                                </div>
                              ) : (
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  onClick={() => setDeleteConfirmId(termin.id)}
                                  disabled={submitting}
                                  style={{ borderRadius: '8px', fontSize: '0.8rem', padding: '6px 12px' }}
                                >
                                  Löschen
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile-Ansicht: Kachel-Kartenliste */}
                <div className="d-lg-none p-3" style={{ backgroundColor: '#fdfbf9' }}>
                  {appointments.map((termin) => {
                    const partner = selectedUser.rolle?.toLowerCase() === 'tutor' ? termin.schueler : termin.tutor;
                    const partnerLabel = selectedUser.rolle?.toLowerCase() === 'tutor' ? 'Schüler' : 'Dozent';

                    return (
                      <div key={termin.id} className="p-3 mb-3 border rounded bg-white shadow-sm d-flex flex-column gap-2" style={{ borderColor: '#eae5e0', borderRadius: '12px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small" style={{ fontWeight: '600' }}>Termin-ID: #{termin.id}</span>
                          <span className="fw-bold" style={{ color: '#2c3e50' }}>{formatPrice(termin.preis)}</span>
                        </div>
                        <div className="fw-bold fs-6 text-dark d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <span style={{ fontFamily: "'Lora', serif" }}>{termin.skillName}</span>
                          <div className="d-flex gap-1">
                            {getStatusBadge(termin.status)}
                            {getZahlungBadge(termin.zahlungsstatus)}
                          </div>
                        </div>
                        <div className="text-muted small mt-1">
                          <i className="bi bi-clock me-1"></i> {formatDateTime(termin.datumZeit)} ({termin.dauerMinuten} Min.)
                        </div>
                        <div className="small mt-1 p-2 bg-light rounded border-start border-3 text-break" style={{ borderColor: '#b95c50' }}>
                          <div className="fw-bold">{partnerLabel}: {partner?.vollerName || 'Unbekannt'}</div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>E-Mail: {partner?.email || '-'} | ID: #{partner?.id || '-'}</div>
                        </div>
                        {termin.beschreibung && (
                          <div className="text-muted small mt-1 p-2 bg-light rounded text-break" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                            "{termin.beschreibung}"
                          </div>
                        )}
                        <div className="d-flex justify-content-end mt-2 pt-2 border-top" style={{ borderColor: '#eae5e0' }}>
                          {deleteConfirmId === termin.id ? (
                            <div className="d-flex gap-2 align-items-center w-100 justify-content-between">
                              <span className="text-danger small fw-bold">Diesen Termin stornieren / löschen?</span>
                              <div className="d-flex gap-2">
                                <Button variant="danger" size="sm" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => handleDeleteTermin(termin.id)} disabled={submitting}>Ja</Button>
                                <Button variant="outline-secondary" size="sm" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => setDeleteConfirmId(null)} disabled={submitting}>Nein</Button>
                              </div>
                            </div>
                          ) : (
                            <Button variant="outline-danger" size="sm" className="w-100" style={{ borderRadius: '8px' }} onClick={() => setDeleteConfirmId(termin.id)} disabled={submitting}>Löschen</Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
